// Les démonstrations animées de « Aide et extension » (Radar, 2026-10-15).
//
// POURQUOI DES SCHÉMAS ET PAS DES CAPTURES. Une capture de l'extension vieillit
// à chaque retouche de son interface, pèse des centaines de kilo-octets, et ne
// suit ni la langue ni le thème sombre. Ces scènes sont dessinées en CSS (les
// classes `.dm-*` d'app.css) : quelques kilo-octets, traduites comme le reste.
// Elles doivent suivre l'extension quand ses libellés changent : les textes
// viennent de `demo.*` dans i18n.ts, recopiés de ceux de l'extension.
//
// TROIS RÈGLES, qui ne se voient pas en relisant :
//  - L'ÉTAT PAR DÉFAUT DE CHAQUE ÉLÉMENT EST L'IMAGE FINALE. Sous « réduire les
//    animations », app.css coupe toutes les animations : on voit alors le
//    résultat (l'icône épinglée, le score affiché), jamais une scène vide.
//  - UNE SCÈNE NE JOUE QUE SI ELLE EST À L'ÉCRAN (IntersectionObserver), et se
//    met en pause d'un clic : WCAG 2.2.2 l'exige pour tout mouvement de plus de
//    cinq secondes qui démarre seul.
//  - Une scène mesure ses textes en `cqw` (unités de conteneur). Le conteneur
//    ne peut pas mesurer SA PROPRE police ainsi : elle est posée sur ses
//    enfants (`.dm-scene > *`), sinon tout s'affiche trois fois trop gros.

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useT, type CleUi } from '../lib/i18n'

function Demo({
  id,
  titre,
  aria,
  etapes,
  hauteur,
  children,
}: {
  id: string
  titre: CleUi
  aria: CleUi
  etapes?: CleUi[]
  /** Scène plus basse que le 16/10 d'un navigateur (la frise). */
  hauteur?: 'basse'
  children: ReactNode
}) {
  const t = useT()
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [enPause, setEnPause] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.35 })
    io.observe(el)
    return () => io.disconnect()
  }, [])
  return (
    <div ref={ref} className={`dm dm-${id}${visible && !enPause ? ' dm-joue' : ''}`}>
      <div className="dm-t">
        <b>{t(titre)}</b>
        <button type="button" className="dm-pause" onClick={() => setEnPause((p) => !p)}>
          {t(enPause ? 'demo.lecture' : 'demo.pause')}
        </button>
      </div>
      <div className={`dm-scene${hauteur === 'basse' ? ' dm-basse' : ''}`} role="img" aria-label={t(aria)}>
        {children}
      </div>
      {etapes && (
        <ol className="dm-etapes">
          {etapes.map((e) => (
            <li key={e}>{t(e)}</li>
          ))}
        </ol>
      )}
      <p className="dm-reduit">{t('demo.reduit')}</p>
    </div>
  )
}

const Curseur = () => (
  <span className="dm-curseur" aria-hidden="true">
    <svg viewBox="0 0 24 24">
      <path d="M5 2l14 11.5-6.2.8 3.6 7.2-2.9 1.4-3.5-7.2L5 20z" fill="#fff" stroke="#15191e" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  </span>
)

const Puzzle = () => (
  <span className="dm-puzzle">
    <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19.4 14.6c.6 0 1.1-.5 1.1-1.1V10c0-.6-.5-1.1-1.1-1.1h-1.6a2 2 0 1 0-4 0H12V7.3a2 2 0 1 0-4 0V9H5.5c-.6 0-1 .4-1 1v3h1.6a2 2 0 1 1 0 4H4.5v3c0 .6.4 1 1 1h3v-1.6a2 2 0 1 1 4 0V21h3c.6 0 1-.4 1-1v-3.4h1.6a2 2 0 1 0 1.3-2z" />
    </svg>
  </span>
)

function Barre({ url, puzzle = true }: { url: ReactNode; puzzle?: boolean }) {
  return (
    <div className="dm-chrome">
      <span className="dm-dots"><i /><i /><i /></span>
      <span className="dm-url">{url}</span>
      <span className="dm-outils">
        <span className="dm-ext">AJ</span>
        {puzzle && <Puzzle />}
      </span>
    </div>
  )
}

const Lignes = ({ l }: { l: number[] }) => (
  <div className="dm-lignes">
    {l.map((w, i) => (
      <i key={i} style={{ width: `${w}%` }} />
    ))}
  </div>
)

/** Installer depuis le Chrome Web Store, puis épingler l'icône. */
export function DemoInstaller() {
  const t = useT()
  return (
    <Demo id="install" titre="demo.install.titre" aria="demo.install.aria" etapes={['demo.install.e1', 'demo.install.e2', 'demo.install.e3']}>
      <Barre url="chromewebstore.google.com" />
      <div className="dm-corps">
        <div className="dm-store">
          <span className="dm-appico">AJ</span>
          <span>
            <b>AI Job Fit</b>
            <small>{t('demo.install.slogan')}</small>
          </span>
          <span className="dm-b dm-p dm-add">{t('aide.step.install.store')}</span>
        </div>
        <Lignes l={[92, 80, 86, 60]} />
      </div>
      <div className="dm-bulle dm-dlg">
        <b>{t('demo.install.dlgTitre')}</b>
        <small>{t('demo.install.dlgTexte')}</small>
        <span className="dm-boutons">
          <span className="dm-b">{t('demo.annuler')}</span>
          <span className="dm-b dm-p">{t('demo.install.ajouter')}</span>
        </span>
      </div>
      <div className="dm-bulle dm-menu">
        <small>{t('demo.install.menu')}</small>
        <span className="dm-row">
          <i className="dm-mini" />
          <span>AI Job Fit</span>
          <svg className="dm-pin" viewBox="0 0 24 24">
            <path fill="currentColor" d="M15 3l6 6-2 1-3 3 .5 4.5L15 19l-3.5-3.5L6 21l-1-1 5.5-5.5L7 11l1.5-1.5L13 10l3-3z" />
          </svg>
        </span>
      </div>
      <Curseur />
    </Demo>
  )
}

/** Connecter l'extension à Radar depuis ses options. */
export function DemoConnecter() {
  const t = useT()
  return (
    <Demo id="co" titre="demo.co.titre" aria="demo.co.aria" etapes={['demo.co.e1', 'demo.co.e2', 'demo.co.e3', 'demo.co.e4']}>
      <div className="dm-onglets">
        <span className="dm-onglet dm-o1">Options · AI Job Fit</span>
        <span className="dm-onglet dm-o2">Radar</span>
      </div>
      <Barre
        puzzle={false}
        url={
          <>
            <span className="dm-u1">AI Job Fit · options</span>
            <span className="dm-u2">radar.ai-job-fit.com</span>
          </>
        }
      />
      <div className="dm-vue dm-va">
        <span className="dm-gras">{t('demo.co.import')}</span>
        <span className="dm-depot">{t('demo.co.depot')}</span>
        <span className="dm-lien">{t('demo.co.lien')}</span>
      </div>
      <div className="dm-vue dm-vb">
        <span className="dm-gras">{t('demo.co.compte')}</span>
        <span><span className="dm-b dm-p">{t('demo.co.bouton')}</span></span>
        <span className="dm-gris">{t('demo.co.ouBien')}</span>
      </div>
      <div className="dm-vue dm-vr">
        <svg className="dm-coche" viewBox="0 0 40 40" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="20" cy="20" r="17" />
          <path d="M12 20.5l5.5 5.5L28.5 15" />
        </svg>
        <span className="dm-gras">{t('aideR.ext.connectee')}</span>
        <span className="dm-gris">{t('demo.co.fermer')}</span>
      </div>
      <div className="dm-vue dm-vc">
        <span className="dm-gras">{t('demo.co.cv')}</span>
        <span className="dm-cv">
          <i />
          <span>
            <b>{t('demo.co.nom')}</b>
            <br />
            <span className="dm-gris">{t('demo.co.metier')}</span>
          </span>
        </span>
        <span className="dm-gris">{t('demo.co.bouton2')}</span>
      </div>
      <div className="dm-bulle dm-perm">
        <b>{t('demo.co.perm')}</b>
        <small>radar.ai-job-fit.com</small>
        <span className="dm-boutons">
          <span className="dm-b">{t('demo.refuser')}</span>
          <span className="dm-b dm-p">{t('demo.autoriser')}</span>
        </span>
      </div>
      <Curseur />
    </Demo>
  )
}

/** Activer l'extension sur un site qu'elle ne connaît pas. */
export function DemoActiver() {
  const t = useT()
  return (
    <Demo id="ac" titre="demo.ac.titre" aria="demo.ac.aria" etapes={['demo.ac.e1', 'demo.ac.e2', 'demo.ac.e3', 'demo.ac.e4']}>
      <Barre url="carrieres.chu-exemple.be/offres/2291" />
      <div className="dm-corps">
        <span className="dm-gras">{t('demo.ac.poste')}</span>
        <span className="dm-gris" style={{ display: 'block' }}>{t('demo.ac.emp')}</span>
        <Lignes l={[55, 50, 58, 44, 52]} />
      </div>
      <div className="dm-bulle dm-pop">
        <b>AI Job Fit</b>
        <small>{t('demo.ac.pasAcces')}</small>
        <span className="dm-b dm-p">{t('demo.ac.bouton')}</span>
      </div>
      <div className="dm-bulle dm-perm">
        <b>{t('demo.ac.perm')}</b>
        <small>carrieres.chu-exemple.be</small>
        <span className="dm-boutons">
          <span className="dm-b">{t('demo.refuser')}</span>
          <span className="dm-b dm-p">{t('demo.autoriser')}</span>
        </span>
      </div>
      <div className="dm-widget">
        <span className="dm-sc">
          <b>78</b>
          <span>{t('demo.ac.profil')}</span>
        </span>
        <span className="dm-b dm-p">{t('demo.ac.adapter')}</span>
        <span className="dm-b">{t('demo.ac.enregistrer')}</span>
      </div>
      <Curseur />
    </Demo>
  )
}

/** Le délai d'un employeur que Radar ne sait pas encore lire. */
export function DemoEmployeur() {
  const t = useT()
  return (
    <Demo id="emp" titre="demo.emp.titre" aria="demo.emp.aria" hauteur="basse">
      <div className="dm-frise"><i /></div>
      <span className="dm-n dm-n1">1</span>
      <span className="dm-n dm-n2">2</span>
      <span className="dm-n dm-n3">3</span>
      <span className="dm-lb dm-l1">{t('demo.emp.l1')}<small>{t('demo.emp.l1s')}</small></span>
      <span className="dm-lb dm-l2">{t('demo.emp.l2')}<small>{t('demo.emp.l2s')}</small></span>
      <span className="dm-lb dm-l3">{t('demo.emp.l3')}<small>{t('demo.emp.l3s')}</small></span>
    </Demo>
  )
}
